import { Button, Typography } from "@mui/material";
import { Box } from "@mui/system";
import Grid from "@mui/material/Grid2";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PairCurrencyMismatch from "./PairCurrencyMismatchModal/PairCurrencyMismatch";
import React from "react";
import { useSelector } from "react-redux";
import { ReceiptsEndPointLogic } from "../../../../../../logic/Finance/ProviderExpense/ReceiptsEndpointLogic";
import { selectUserToken } from "../../../../../../features/userSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import SimpleBackdrop from "../../../../../BackDrop/BackDrop";

const PairingRecordsChosenHead = ({ mismatchedReceipts, rows }: any) => {
  const [openModal, setOpenModal] = React.useState(false);
  const { linkedFmesByReceipt } = useSelector((state: any) => state.receipts);
  const [loading, setLoading] = React.useState(false);
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const navigate = useNavigate();

  const handleRedirectBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("receipt_shared_uid");

    navigate({
      pathname: "/finance/receipts",
      search: params.toString(),
    });
  };

  const buildTransactionPayloads = () => {
    const createPayload = { receipts: [] as any[] };
    const deletePayloads: {
      receipt_shared_uid: string;
      fme_shared_uids: string[];
    }[] = [];

    rows.forEach((receipt: any) => {
      const receiptUid = receipt.receipt_shared_uid;

      const original = (receipt.fmes || []).map((f: any) => f.fme_shared_uid);

      const current = (linkedFmesByReceipt[receiptUid] || []).map(
        (f: any) => f.shared_uid,
      );

      const toDelete = original.filter((uid: string) => !current.includes(uid));

      const toCreate = current.filter((uid: string) => !original.includes(uid));

      if (toDelete.length > 0) {
        deletePayloads.push({
          receipt_shared_uid: receiptUid,
          fme_shared_uids: toDelete,
        });
      }

      if (toCreate.length > 0) {
        createPayload.receipts.push({
          receipt_shared_uid: receiptUid,
          fme_shared_uids: toCreate,
        });
      }
    });

    return { createPayload, deletePayloads };
  };
  const { createPayload, deletePayloads } = buildTransactionPayloads();
  const isSaveEnabled =
    createPayload.receipts.length > 0 || deletePayloads.length > 0;
  const submitPairing = async () => {
    try {
      setLoading(true);
      const { createPayload, deletePayloads } = buildTransactionPayloads();

      for (const payload of deletePayloads) {
        const res = await ReceiptsEndPointLogic.deleteFinancialMonthlyEarnings(
          oauthTokenHeader,
          payload,
        );

        if (res?.status !== 200) {
          throw new Error("Delete API failed");
        }
      }

      // 2️⃣ Create phase
      if (createPayload.receipts.length > 0) {
        const createRes =
          await ReceiptsEndPointLogic.createFinancialMonthlyEarnings(
            oauthTokenHeader,
            createPayload,
          );

        if (createRes?.status !== 200) {
          throw new Error("Create API failed");
        }
      }

      // Only if everything succeeds
      handleRedirectBack();
    } catch (error) {
      console.error("Error during pairing submission:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndUpload = async () => {
    if (mismatchedReceipts.length > 0) {
      setOpenModal(true);
      return;
    }
    await submitPairing();
  };
  const handleContinueAnyway = async () => {
    setOpenModal(false);
    await submitPairing();
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <Box sx={{ padding: " 16px " }}>
        {loading ? <SimpleBackdrop /> : null}
        <Grid container spacing={2} alignItems="center">
          <Grid size={7}>
            <Typography variant="h3" sx={{ fontSize: "24px" }}>
              Receipts Pairing
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }}>
              Match Receipts to FME records and keep track of your payments.
            </Typography>
          </Grid>
          <Grid
            size={5}
            container
            spacing={2}
            flexDirection={"row"}
            justifyContent={"flex-end"}
          >
            <Button
              variant="contained"
              color="secondary"
              sx={{ whiteSpace: "nowrap" }}
              endIcon={<SaveOutlinedIcon />}
              onClick={handleSaveAndUpload}
              disabled={!isSaveEnabled}
              data-tracker="591"
            >
              SAVE & UPLOAD
            </Button>
            <PairCurrencyMismatch
              open={openModal}
              handleClose={handleCloseModal}
              rows={mismatchedReceipts}
              onContinue={handleContinueAnyway}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default PairingRecordsChosenHead;
